import React from 'react';
import { logger } from '@/lib/logger';
import { Button } from '@/components/ui/button';

export class AppErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    logger.error(error, { componentStack: info.componentStack });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-8">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-10 text-center max-w-md">
            <h2 className="text-2xl font-bold text-white mb-3">Something went wrong</h2>
            <p className="text-slate-400 mb-6">An unexpected error occurred. Please refresh and try again.</p>
            <Button onClick={() => window.location.reload()} className="bg-cyan-500 hover:bg-cyan-600">
              Reload Page
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export class PageErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    logger.error(error, { componentStack: info.componentStack });
  }

  componentDidUpdate(prevProps) {
    // The boundary persists across route changes (no key prop) so it can catch
    // errors thrown during unmount of the previous page — e.g. the Polotno
    // canvas throwing during teardown when navigating away from the Design
    // Studio. When the page changes, clear any error so the new page renders
    // normally instead of being stuck on the fallback.
    if (this.state.hasError && prevProps.currentPageName !== this.props.currentPageName) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-10 text-center max-w-md">
            <h2 className="text-2xl font-bold text-white mb-3">This page encountered an error</h2>
            <p className="text-slate-400 mb-6">Something went wrong. You can try again or navigate to another page.</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={this.handleReset} className="border-slate-700 text-slate-400 hover:bg-white/10 hover:text-white">
                Try Again
              </Button>
              <Button onClick={() => { window.location.href = '/Dashboard'; }} className="bg-cyan-500 hover:bg-cyan-600">
                Go Home
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;